import React, {
  FC,
  useRef,
  useEffect,
  Fragment,
  Dispatch,
  SetStateAction,
} from "react";
import { HStack, Pressable, Text, VStack, Divider } from "native-base";
import { Dimensions } from "react-native";
import { Loc } from "../../App/Loc";
import "driver.js/dist/driver.css";
import { driver } from "driver.js";

interface MenuItem {
  id: string;
  name: string;
  fn: () => void;
  flag?: boolean;
}

interface TabHeaderProps {
  menu: any;
  MoreMenu: any;
  isDropDown: boolean;
  setIsDropDown: Dispatch<SetStateAction<boolean>>;
  type: string | undefined;
  Entity: any;
  userLang?: string;
  onboardingFlow?: string | null;
  onboardingStep?: number;
  setOnboardingStep?: Dispatch<SetStateAction<number>>;
  clearCurrentTourFlow?: () => void;
}

const TabHeader: FC<TabHeaderProps> = ({
  menu,
  isDropDown,
  MoreMenu,
  setIsDropDown,
  type,
  Entity,
  userLang,
  onboardingFlow,
  onboardingStep,
  setOnboardingStep,
  clearCurrentTourFlow,
}) => {
  /* -------------------- TOUR REFS -------------------- */
  const moreBtnRef = useRef<HTMLDivElement | null>(null);
  const membersRef = useRef<HTMLDivElement | null>(null);
  const playersRef = useRef<HTMLDivElement | null>(null);
  const tabActionRef = useRef<(() => void) | null>(null);

  /* -------------------- CAPTURE TAB ACTION -------------------- */
  useEffect(() => {
    if (!onboardingFlow) return;

    const targetName =
      onboardingFlow === "teamMember" ||
      onboardingFlow === "clubMember" ||
      onboardingFlow === "clubTermMember" ||
      onboardingFlow === "eliteClubMember" ||
      onboardingFlow === "groupMember" ||
      onboardingFlow === "communityMember"
        ? Loc.currentLang.menu.members
        : onboardingFlow === "schoolStaff"
          ? Loc.currentLang.menu.staff
          : onboardingFlow === "teamSeasonPlayer" ||
              onboardingFlow === "performancePlayer"
            ? Loc.currentLang.menu.players
            : onboardingFlow === "schoolYearStudent" ||
                onboardingFlow === "classStudent" ||
                onboardingFlow === "gradClassStudent"
              ? Loc.currentLang.menu.students
              : onboardingFlow === "organizationEmployee"
                ? Loc.currentLang.menu.employees
                : onboardingFlow === "neighborhoodNeighbors"
                  ? Loc.currentLang.menu.neighbors
                  : null;

    if (!targetName) return;

    const tabItem = MoreMenu.find((item) => item?.name === targetName);

    if (tabItem?.fn) {
      tabActionRef.current = tabItem.fn;
    }
  }, [isDropDown, MoreMenu, onboardingFlow]);

  /* -------------------- TOUR : MORE BUTTON -------------------- */
  useEffect(() => {
    if (!moreBtnRef.current) return;
    if (onboardingStep !== 1) return;
    if (
      onboardingFlow !== "teamMember" &&
      onboardingFlow !== "clubMember" &&
      onboardingFlow !== "eliteClubMember" &&
      onboardingFlow !== "schoolStaff"
    )
      return;

    const tour = driver({
      showProgress: false,
      allowClose: true,
      overlayClickBehavior: () => {},
      showButtons: [],
      nextBtnText: Loc.currentLang.inviteTourGuide.next,
      doneBtnText: Loc.currentLang.inviteTourGuide.next,
      prevBtnText: Loc.currentLang.inviteTourGuide.previous,
      onCloseClick: () => {
        tour.destroy();
        clearCurrentTourFlow?.();
      },
      steps: [
        {
          element: moreBtnRef.current,
          popover: {
            title: Loc.currentLang.inviteTourGuide.step4_1,
            description: `${Loc.currentLang.inviteTourGuide.moreOptions} ${
              onboardingFlow === "schoolStaff"
                ? Loc.currentLang.inviteTourGuide.staff
                : Loc.currentLang.inviteTourGuide.members
            }.`,
            side: "bottom",
            align: "center",
            onNextClick: () => {
              setIsDropDown(true);
              setOnboardingStep?.(2);
              setTimeout(() => tour.destroy(), 0);
            },
          },
        },
      ],
      onPopoverRender: (popover) => {
        const footer = popover.wrapper.querySelector(".driver-popover-footer");

        if (footer) {
          const skipBtn = document.createElement("button");
          skipBtn.innerText = Loc.currentLang.inviteTourGuide.skip;

          skipBtn.style.marginRight = "auto";
          skipBtn.style.background = "transparent";
          skipBtn.style.border = "none";
          skipBtn.style.color = "#6B7280";
          skipBtn.style.fontWeight = "500";
          skipBtn.style.cursor = "pointer";

          skipBtn.onclick = () => {
            tour.destroy();
            clearCurrentTourFlow?.();
          };

          footer.prepend(skipBtn);
        }
      },
    });

    setTimeout(() => tour.drive(), 120);
  }, [onboardingFlow, onboardingStep]);

  /* -------------------- TOUR : MEMBER/PLAYER TAB MENU -------------------- */
  useEffect(() => {
    if (
      (onboardingFlow === "teamMember" ||
        onboardingFlow === "clubMember" ||
        onboardingFlow === "eliteClubMember" ||
        onboardingFlow === "schoolStaff") &&
      onboardingStep !== 2
    )
      return;
    if (
      (onboardingFlow === "teamSeasonPlayer" ||
        onboardingFlow === "performancePlayer" ||
        onboardingFlow === "clubTermMember" ||
        onboardingFlow === "schoolYearStudent" ||
        onboardingFlow === "groupMember" ||
        onboardingFlow === "classStudent" ||
        onboardingFlow === "gradClassStudent" ||
        onboardingFlow === "organizationEmployee" ||
        onboardingFlow === "neighborhoodNeighbors") &&
      onboardingStep !== 1
    )
      return;
    if (!onboardingFlow) return;

    const element =
      onboardingFlow === "teamMember" ||
      onboardingFlow === "clubMember" ||
      onboardingFlow === "eliteClubMember" ||
      onboardingFlow === "schoolStaff"
        ? membersRef.current
        : onboardingFlow === "teamSeasonPlayer" ||
            onboardingFlow === "performancePlayer" ||
            onboardingFlow === "clubTermMember" ||
            onboardingFlow === "schoolYearStudent" ||
            onboardingFlow === "groupMember" ||
            onboardingFlow === "classStudent" ||
            onboardingFlow === "gradClassStudent" ||
            onboardingFlow === "organizationEmployee" ||
            onboardingFlow === "neighborhoodNeighbors" ||
            onboardingFlow === "communityMember"
          ? playersRef.current
          : null;

    const tour = driver({
      showProgress: false,
      allowClose: true,
      overlayClickBehavior: () => {},
      showButtons: [],
      onCloseClick: () => {
        tour.destroy();
        clearCurrentTourFlow?.();
      },
      steps: [
        {
          element: element as Element,
          popover: {
            title:
              onboardingFlow === "teamMember" ||
              onboardingFlow === "clubMember" ||
              onboardingFlow === "eliteClubMember" ||
              onboardingFlow === "schoolStaff"
                ? Loc.currentLang.inviteTourGuide.step4_2
                : onboardingFlow === "teamSeasonPlayer" ||
                    onboardingFlow === "clubTermMember" ||
                    onboardingFlow === "schoolYearStudent" ||
                    onboardingFlow === "groupMember" ||
                    onboardingFlow === "classStudent" ||
                    onboardingFlow === "gradClassStudent" ||
                    onboardingFlow === "organizationEmployee" ||
                    onboardingFlow === "neighborhoodNeighbors" ||
                    onboardingFlow === "communityMember" ||
                    onboardingFlow === "performancePlayer"
                  ? Loc.currentLang.inviteTourGuide.step3_1
                  : "TabHeaderStep",
            description: `${Loc.currentLang.inviteTourGuide.manageYour} ${
              onboardingFlow === "teamMember" ||
              onboardingFlow === "clubMember" ||
              onboardingFlow === "clubTermMember" ||
              onboardingFlow === "eliteClubMember" ||
              onboardingFlow === "groupMember" ||
              onboardingFlow === "communityMember"
                ? Loc.currentLang.inviteTourGuide.members
                : onboardingFlow === "teamSeasonPlayer" ||
                    onboardingFlow === "performancePlayer" ||
                    onboardingFlow === "clubTermMember"
                  ? Loc.currentLang.inviteTourGuide.players
                  : onboardingFlow === "schoolStaff"
                    ? Loc.currentLang.inviteTourGuide.staff
                    : onboardingFlow === "schoolYearStudent" ||
                        onboardingFlow === "classStudent" ||
                        onboardingFlow === "gradClassStudent"
                      ? Loc.currentLang.inviteTourGuide.students
                      : onboardingFlow === "organizationEmployee"
                        ? Loc.currentLang.inviteTourGuide.employees
                        : onboardingFlow === "neighborhoodNeighbors"
                          ? Loc.currentLang.inviteTourGuide.neighbors
                          : "Manage"
            } ${Loc.currentLang.inviteTourGuide.viewExisting} ${
              onboardingFlow === "teamMember" ||
              onboardingFlow === "clubMember" ||
              onboardingFlow === "clubTermMember" ||
              onboardingFlow === "eliteClubMember" ||
              onboardingFlow === "groupMember" ||
              onboardingFlow === "communityMember"
                ? Loc.currentLang.inviteTourGuide.members
                : onboardingFlow === "teamSeasonPlayer" ||
                    onboardingFlow === "performancePlayer" ||
                    onboardingFlow === "clubTermMember"
                  ? Loc.currentLang.inviteTourGuide.players
                  : onboardingFlow === "schoolStaff"
                    ? Loc.currentLang.inviteTourGuide.staff
                    : onboardingFlow === "schoolYearStudent" ||
                        onboardingFlow === "classStudent" ||
                        onboardingFlow === "gradClassStudent"
                      ? Loc.currentLang.inviteTourGuide.students
                      : onboardingFlow === "organizationEmployee"
                        ? Loc.currentLang.inviteTourGuide.employees
                        : onboardingFlow === "neighborhoodNeighbors"
                          ? Loc.currentLang.inviteTourGuide.neighbors
                          : "Manage"
            } ${Loc.currentLang.inviteTourGuide.inviteNew}`,
            side: "right",
            align: "start",
            //onNextClick: () => {
            //  tour.destroy();
            //  setIsDropDown(false);
            //  tabActionRef.current?.();
            //},
          },
        },
      ],
      onPopoverRender: (popover) => {
        const footer = popover.wrapper.querySelector(
          ".driver-popover-footer",
        ) as HTMLElement;
        if (!footer) return;

        footer.innerHTML = "";
        footer.style.display = "flex";
        footer.style.justifyContent = "space-between";

        /* Skip */
        const skip = document.createElement("button");
        skip.innerText = Loc.currentLang.inviteTourGuide.skip;
        skip.className = "tour-btn-secondary";
        skip.onclick = () => {
          tour.destroy();
          clearCurrentTourFlow?.();
        };

        /* Right container */
        const right = document.createElement("div");
        right.style.display = "flex";
        right.style.gap = "8px";

        /* Previous */
        if (
          onboardingFlow === "teamMember" ||
          onboardingFlow === "clubMember" ||
          onboardingFlow === "eliteClubMember" ||
          onboardingFlow === "schoolStaff"
        ) {
          const prev = document.createElement("button");
          prev.innerText = Loc.currentLang.inviteTourGuide.previous;
          prev.className = "tour-btn-secondary";
          prev.onclick = () => {
            tour.destroy();
            setIsDropDown(false);
            setOnboardingStep?.(1);
          };

          right.appendChild(prev);
        }

        /* Next */
        const next = document.createElement("button");
        next.innerText = Loc.currentLang.inviteTourGuide.next;
        next.className = "tour-btn-primary";
        next.onclick = () => {
          tour.destroy();
          tabActionRef.current?.();
          if (
            onboardingFlow === "teamMember" ||
            onboardingFlow === "clubMember" ||
            onboardingFlow === "eliteClubMember" ||
            onboardingFlow === "schoolStaff"
          ) {
            setIsDropDown(false);
            setOnboardingStep?.(3);
          } else if (
            onboardingFlow === "teamSeasonPlayer" ||
            onboardingFlow === "performancePlayer" ||
            onboardingFlow === "clubTermMember" ||
            onboardingFlow === "schoolYearStudent" ||
            onboardingFlow === "groupMember" ||
            onboardingFlow === "classStudent" ||
            onboardingFlow === "gradClassStudent" ||
            onboardingFlow === "organizationEmployee" ||
            onboardingFlow === "neighborhoodNeighbors" ||
            onboardingFlow === "communityMember"
          ) {
            setOnboardingStep?.(2);
          }
        };

        right.appendChild(next);
        footer.append(skip, right);
      },
    });

    setTimeout(() => tour.drive(), 120);
  }, [onboardingFlow, onboardingStep /*, isDropDown*/]);

  const menuRefs = useRef(null);
  const dimensions = Dimensions.get("window");
  const fourhundred = dimensions?.width <= 400 || false;
  const fiveSeventy = dimensions?.width <= 570 || false;
  const sevenSeventy = dimensions?.width <= 770 || false;
  const ninesixty = dimensions?.width <= 960 || false;
  const thousandthirty = dimensions?.width <= 1030 || false;
  const thousandfivty = dimensions?.width <= 1260 || false;
  const thousandSixty = dimensions?.width <= 1650 || false;
  const thousandEighty = dimensions?.width <= 1850 || false;
  const twothousand = dimensions?.width <= 2000 || false;
  const twothousandtwo = dimensions?.width <= 2200 || false;
  const twothousandmore = dimensions?.width >= 2200 || false;
  const numberOfTabShowing: number = fourhundred
    ? 3
    : fiveSeventy
      ? 4
      : sevenSeventy
        ? 5
        : ninesixty
          ? 6
          : thousandthirty
            ? 3
            : thousandfivty && Entity?.type !== "Person"
              ? 4
              : thousandSixty &&
                  (Entity?.type === "Group" ||
                    Entity?.type === "SchoolYear" ||
                    Entity?.type === "Community" ||
                    Entity?.type === "Organization") &&
                  Entity?.subType === undefined
                ? 5
                : thousandEighty &&
                    (Entity?.type === "Group" ||
                      Entity?.type === "SchoolYear" ||
                      Entity?.type === "Community" ||
                      Entity?.type === "Organization") &&
                    Entity?.subType === undefined
                  ? 5
                  : thousandSixty &&
                      Entity?.type !== "Neighborhood" &&
                      Entity?.type !== "Person" &&
                      Entity?.type !== "Event" &&
                      Entity?.type !== "Game" &&
                      Entity?.subType !== "Term" &&
                      Entity?.subType !== "Department" &&
                      Entity?.subType !== "GradClass"
                    ? userLang !== "en" && userLang !== "en-us"
                      ? 5
                      : 6
                    : thousandEighty &&
                        Entity?.type !== "Neighborhood" &&
                        Entity?.type !== "Person" &&
                        Entity?.type !== "Event" &&
                        Entity?.type !== "Game" &&
                        Entity?.subType !== "Term" &&
                        Entity?.subType !== "Department" &&
                        Entity?.subType !== "GradClass"
                      ? userLang !== "en" && userLang !== "en-us"
                        ? 5
                        : 6
                      : Entity?.type == "Neighborhood" && thousandEighty
                        ? 5
                        : twothousandtwo && Entity?.type === "Club"
                          ? 7
                          : twothousand && Entity?.type === "Club"
                            ? 7
                            : twothousandtwo && Entity?.type === "School"
                              ? 6
                              : twothousand && Entity?.type === "School"
                                ? 6
                                : twothousand &&
                                    (Entity?.type === "Group" ||
                                      Entity?.type === "SchoolYear" ||
                                      Entity?.type === "Community" ||
                                      Entity?.type === "Event" ||
                                      Entity?.type === "Game" ||
                                      Entity?.type === "Organization" ||
                                      Entity?.subType === "Term" ||
                                      Entity?.subType === "Department" ||
                                      Entity?.subType === "GradClass")
                                  ? 5
                                  : twothousand &&
                                      Entity?.type !== "Neighborhood" &&
                                      Entity?.type !== "Person"
                                    ? 6
                                    : twothousandtwo &&
                                        (Entity?.type === "Group" ||
                                          Entity?.type === "SchoolYear" ||
                                          Entity?.type === "Community" ||
                                          Entity?.type === "Event" ||
                                          Entity?.type === "Game" ||
                                          Entity?.type === "Organization" ||
                                          Entity?.subType === "Term" ||
                                          Entity?.subType === "Department" ||
                                          Entity?.subType === "GradClass")
                                      ? 5
                                      : twothousandtwo &&
                                          Entity?.type !== "Neighborhood" &&
                                          Entity?.type !== "Person"
                                        ? 6
                                        : twothousandmore
                                          ? 6
                                          : 6;
  const Start = fourhundred
    ? 3
    : fiveSeventy
      ? 4
      : sevenSeventy
        ? 5
        : ninesixty
          ? 6
          : thousandthirty
            ? 3
            : thousandfivty && Entity?.type !== "Person"
              ? 4
              : thousandSixty &&
                  (Entity?.type === "Group" ||
                    Entity?.type === "SchoolYear" ||
                    Entity?.type === "Community" ||
                    Entity?.type === "Organization") &&
                  Entity?.subType === undefined
                ? 5
                : thousandEighty &&
                    (Entity?.type === "Group" ||
                      Entity?.type === "SchoolYear" ||
                      Entity?.type === "Community" ||
                      Entity?.type === "Organization") &&
                    Entity?.subType === undefined
                  ? 5
                  : thousandSixty &&
                      Entity?.type !== "Neighborhood" &&
                      Entity?.type !== "Person" &&
                      Entity?.type !== "Event" &&
                      Entity?.type !== "Game" &&
                      Entity?.subType !== "Term" &&
                      Entity?.subType !== "Department" &&
                      Entity?.subType !== "GradClass"
                    ? userLang !== "en" && userLang !== "en-us"
                      ? 5
                      : 6
                    : thousandEighty &&
                        Entity?.type !== "Neighborhood" &&
                        Entity?.type !== "Person" &&
                        Entity?.type !== "Event" &&
                        Entity?.type !== "Game" &&
                        Entity?.subType !== "Term" &&
                        Entity?.subType !== "Department" &&
                        Entity?.subType !== "GradClass"
                      ? userLang !== "en" && userLang !== "en-us"
                        ? 5
                        : 6
                      : Entity?.type === "Neighborhood" &&
                          //||Entity?.type === "Event"
                          thousandEighty
                        ? 5
                        : twothousand && Entity?.type === "Club"
                          ? //||Entity?.type === "School" ||
                            //Entity?.type === "SchoolYear" ||
                            //Entity?.type === "Community" ||
                            //Entity?.type === "Organization" ||
                            //(Entity?.type === "Group" && Entity?.subType === undefined)
                            7
                          : twothousandtwo && Entity?.type === "Club"
                            ? //||Entity?.type === "School" ||
                              //Entity?.type === "SchoolYear" ||
                              //Entity?.type === "Community" ||
                              //Entity?.type === "Organization" ||
                              //(Entity?.type === "Group" && Entity?.subType === undefined)
                              7
                            : twothousandtwo && Entity?.type === "School"
                              ? 6
                              : twothousand && Entity?.type === "School"
                                ? 6
                                : twothousand &&
                                    (Entity?.type === "Group" ||
                                      Entity?.type === "SchoolYear" ||
                                      Entity?.type === "Community" ||
                                      Entity?.type === "Organization" ||
                                      Entity?.type === "Event" ||
                                      Entity?.type === "Game" ||
                                      Entity?.subType === "Term" ||
                                      Entity?.subType === "Department" ||
                                      Entity?.subType === "GradClass")
                                  ? 5
                                  : twothousand &&
                                      Entity?.type !== "Neighborhood" &&
                                      Entity?.type !== "Person"
                                    ? 6
                                    : twothousandtwo &&
                                        (Entity?.type === "Group" ||
                                          Entity?.type === "SchoolYear" ||
                                          Entity?.type === "Community" ||
                                          Entity?.type === "Organization" ||
                                          Entity?.type === "Event" ||
                                          Entity?.type === "Game" ||
                                          Entity?.subType === "Term" ||
                                          Entity?.subType === "Department" ||
                                          Entity?.subType === "GradClass")
                                      ? 5
                                      : twothousandtwo &&
                                          Entity?.type !== "Neighborhood" &&
                                          Entity?.type !== "Person"
                                        ? 6
                                        : twothousandmore
                                          ? 6
                                          : 6;
  const End = MoreMenu?.length;

  function useOutsideAlerter(ref) {
    React.useEffect(() => {
      let isMounted = true;
      if (isMounted) {
        function handleClickOutside(event) {
          if (ref.current && !ref.current.contains(event.target)) {
            setIsDropDown(false);
          }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
      return () => {
        isMounted = false;
      };
    }, [ref]);
  }
  useOutsideAlerter(menuRefs);

  return menu?.length > 0 ? (
    <HStack width="100%">
      <HStack
        width={MoreMenu?.slice(Start, End)?.length == 0 ? "100%" : "80%"}
        paddingLeft={"12.5px"}
        overflow={"hidden"}
      >
        {menu?.slice(0, numberOfTabShowing)?.map((item, index) => {
          const isPlayers =
            onboardingFlow === "teamSeasonPlayer" ||
            onboardingFlow === "performancePlayer"
              ? item?.name === Loc.currentLang.menu.players
              : onboardingFlow === "clubTermMember" ||
                  onboardingFlow === "groupMember" ||
                  onboardingFlow === "communityMember"
                ? item?.name === Loc.currentLang.menu.members
                : onboardingFlow === "schoolYearStudent" ||
                    onboardingFlow === "classStudent" ||
                    onboardingFlow === "gradClassStudent"
                  ? item?.name === Loc.currentLang.menu.students
                  : onboardingFlow === "organizationEmployee"
                    ? item?.name === Loc.currentLang.menu.employees
                    : onboardingFlow === "neighborhoodNeighbors"
                      ? item?.name === Loc.currentLang.menu.neighbors
                      : false;

          return (
            //<div key={item?.id + index} ref={isPlayers ? playersRef : null}>
            <Pressable
              key={item?.id + index}
              ref={isPlayers ? playersRef : null}
              onPress={item.fn}
              borderBottomWidth={item.flag ? 4 : 0}
              borderBottomColor="#32AE78"
              marginRight={"36px"}
              alignItems={"center"}
              justifyContent={"center"}
              _hover={{
                borderBottomWidth: 4,
              }}
            >
              <Text
                fontSize={"18px"}
                fontWeight={"500"}
                fontFamily={"Roboto"}
                color={item.flag ? "#000000" : "rgba(0,0,0,0.5)"}
              >
                {item.name}
              </Text>
            </Pressable>
            //</div>
          );
        })}
      </HStack>

      {((sevenSeventy && (type === "Event" || type === "Game")) ||
        (thousandEighty && (type === "Event" || type === "Game")) ||
        (type !== "Event" && type !== "Game") ||
        thousandfivty) && (
        /*MOREMENU*/
        <VStack
          width={MoreMenu?.slice(Start, End)?.length == 0 ? "" : "20%"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          {MoreMenu?.slice(Start, End)?.length > 0 && (
            <div ref={moreBtnRef}>
              <Pressable
                height={isDropDown ? "40px" : ""}
                width={
                  isDropDown && !fiveSeventy
                    ? "113px"
                    : fiveSeventy && isDropDown
                      ? "80px"
                      : ""
                }
                background={isDropDown ? "rgba(0, 0, 0, 0.05)" : ""}
                borderRadius={isDropDown ? "19px" : ""}
                alignItems={"center"}
                justifyContent={"center"}
                onPress={() => setIsDropDown(!isDropDown)}
              >
                <Text
                  fontSize={"18px"}
                  fontWeight={"500"}
                  fontFamily={"Roboto"}
                  color={"rgba(0,0,0,0.5)"}
                >
                  {Loc.currentLang.menu.more}
                </Text>
              </Pressable>
            </div>
          )}

          {isDropDown && (
            <VStack
              ref={menuRefs}
              width={"128px"}
              height="auto"
              shadow={2}
              background={"#FFFFFF"}
              position={"absolute"}
              top={"57px"}
              right={fiveSeventy ? 0 : ""}
              zIndex={999}
              borderRadius={"6px"}
            >
              {MoreMenu?.slice(Start, End)?.map((item, index) => {
                const isMembers =
                  onboardingFlow === "teamMember" && Entity?.type === "Club"
                    ? item?.name === Loc.currentLang.menu.members
                    : onboardingFlow === "schoolStaff"
                      ? item?.name === Loc.currentLang.menu.staff
                      : item?.name === Loc.currentLang.menu.members;

                return (
                  <Fragment key={item?.id + index}>
                    <div ref={isMembers ? membersRef : null}>
                      <Pressable
                        onPress={item?.fn}
                        width={"100%"}
                        height={"36px"}
                        alignItems={"flex-start"}
                        paddingX={"10px"}
                        _hover={{
                          backgroundColor: "gray.100",
                        }}
                        justifyContent={"center"}
                      >
                        <Text fontSize={"14px"} fontFamily={"Roboto"}>
                          {item?.name}
                        </Text>
                      </Pressable>
                    </div>
                    <Divider key={`divider-${item?.id + index}`} />
                  </Fragment>
                );
              })}
            </VStack>
          )}
        </VStack>
      )}
    </HStack>
  ) : (
    <HStack></HStack>
  );
};

export default React.memo(TabHeader);
